import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Loader2, AlertTriangle, ChevronRight, User, Phone } from 'lucide-react';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from '../../components/ui/sheet';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const _fmtDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return isNaN(d) ? iso : d.toLocaleString([], { dateStyle: 'short', timeStyle: 'short' });
};

/**
 * Governance Alerts drill-down slide-over.
 *
 * Opened when the manager clicks the "Governance Alerts" KPI or the
 * "N calls with compliance violations" banner on Manager Ops. Shows a
 * top summary block (rule breakdown, top offenders) then a scrollable
 * per-call list with quick jump to call detail.
 */
export const GovernanceAlertsSheet = ({ open, onOpenChange, domainId, days = 30 }) => {
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    const p = new URLSearchParams();
    if (domainId) p.set('domain_id', domainId);
    p.set('days', String(days));
    p.set('limit', '200');
    axios.get(`${API}/analytics/governance-alerts?${p.toString()}`)
      .then(r => { if (!cancelled) setPayload(r.data); })
      .catch(() => { if (!cancelled) toast.error('Could not load governance alerts.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, domainId, days]);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-2xl overflow-y-auto"
        data-testid="governance-alerts-sheet"
      >
        <SheetHeader className="pb-4 border-b border-slate-100">
          <SheetTitle className="text-lg font-bold text-slate-900 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-rose-600" />
            Governance Alerts
          </SheetTitle>
          <SheetDescription className="text-xs text-slate-500">
            Last {days} days · {payload?.total ?? '…'} call{payload?.total === 1 ? '' : 's'} with compliance violations
          </SheetDescription>
        </SheetHeader>

        {loading ? (
          <div className="py-12 flex items-center justify-center text-slate-500">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
          </div>
        ) : !payload || payload.total === 0 ? (
          <div className="py-12 text-center">
            <p className="text-sm text-emerald-700 font-semibold">Clean sheet — no governance violations in this window.</p>
          </div>
        ) : (
          <div className="pt-4 space-y-6">
            {/* --- Summary block --- */}
            <section className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="rounded-lg border border-rose-100 bg-rose-50 p-3">
                <p className="text-[10px] uppercase tracking-wider text-rose-800 font-bold mb-2">Top rules fired</p>
                {payload.summary?.top_rules?.length ? (
                  <ul className="space-y-1 text-xs text-rose-900">
                    {payload.summary.top_rules.map((r) => (
                      <li key={r.rule} className="flex items-start gap-2" data-testid={`gov-rule-${r.rule.slice(0, 20)}`}>
                        <span className="font-mono bg-rose-200 text-rose-900 text-[10px] px-1.5 py-0.5 rounded flex-shrink-0 min-w-[28px] text-center">
                          {r.count}
                        </span>
                        <span className="line-clamp-2">{r.rule}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-xs italic text-rose-600">No rule breakdown available.</p>
                )}
              </div>
              <div className="rounded-lg border border-amber-100 bg-amber-50 p-3">
                <p className="text-[10px] uppercase tracking-wider text-amber-800 font-bold mb-2">Top offenders</p>
                {payload.summary?.top_agents?.length ? (
                  <ul className="space-y-1 text-xs text-amber-900">
                    {payload.summary.top_agents.map((a) => (
                      <li key={a.agent_id} className="flex items-center gap-2" data-testid={`gov-agent-${a.agent_id}`}>
                        <span className="font-mono bg-amber-200 text-amber-900 text-[10px] px-1.5 py-0.5 rounded min-w-[28px] text-center">
                          {a.count}
                        </span>
                        <User className="w-3 h-3 flex-shrink-0" />
                        <span className="font-semibold">{a.agent_name || a.agent_id}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-xs italic text-amber-700">No agent breakdown available.</p>
                )}
              </div>
            </section>

            {/* --- Per-call list --- */}
            <section>
              <h3 className="text-xs font-bold uppercase tracking-wider text-slate-600 mb-2">
                All flagged calls ({payload.total})
              </h3>
              <ul className="divide-y divide-slate-100">
                {payload.calls.map((c) => (
                  <li
                    key={c.call_id}
                    className="py-3 hover:bg-slate-50 -mx-6 px-6 cursor-pointer group"
                    onClick={() => navigate(`/calls/${c.call_id}`)}
                    data-testid={`gov-call-${c.call_id}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2 mb-1 text-xs">
                          <span className="text-slate-500 tabular-nums">{_fmtDate(c.call_date)}</span>
                          <span className="font-semibold text-slate-800">
                            {c.agent_name || c.agent_id || '—'}
                          </span>
                          {c.customer_phone && (
                            <span className="text-slate-500 inline-flex items-center gap-1">
                              <Phone className="w-3 h-3" />{c.customer_phone}
                            </span>
                          )}
                          {typeof c.overall_score === 'number' && (
                            <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${
                              c.overall_score < 40 ? 'bg-rose-100 text-rose-700' :
                              c.overall_score < 70 ? 'bg-amber-100 text-amber-700' :
                              'bg-emerald-100 text-emerald-700'
                            }`}>
                              Score {c.overall_score}
                            </span>
                          )}
                        </div>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {(c.governance_violations || []).slice(0, 3).map((v, i) => (
                            <span
                              key={i}
                              className="text-[10px] bg-rose-100 text-rose-800 px-1.5 py-0.5 rounded line-clamp-1 max-w-[280px]"
                              title={String(v)}
                            >
                              {String(v)}
                            </span>
                          ))}
                          {(c.governance_violations || []).length > 3 && (
                            <span className="text-[10px] text-slate-500">
                              +{c.governance_violations.length - 3} more
                            </span>
                          )}
                        </div>
                      </div>
                      <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-slate-500 flex-shrink-0 mt-1" />
                    </div>
                  </li>
                ))}
              </ul>
            </section>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default GovernanceAlertsSheet;
